import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisRoles } from '@adornis/users/db/a-roles.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { ValidationMeta } from '@adornis/validation/validator.js';
import { RoleFilter } from '../filter/RoleFilter.js';
import type { AllPermissions } from '../permissions.js';
import { Role } from './Role.js';
import { SemanticRelationship } from './SemanticRelationship.js';

/**
 * @entity ChancenRole
 * The ChancenRole has the name which additionally works as the _id. This way it can be referenced over the _id
 */
@Entity()
export class ChancenRole extends AdornisRoles<AllPermissions> {
  public static override _class = 'ChancenRole';

  constructor(doc: PartialEntityData<ChancenRole>) {
    super(doc);
  }

  // Public name
  @validate(nonOptional())
  @Field(() => String)
  public readableName: Maybe<string>;

  // Under which other names can the role be found. Internal employees might have different names for some roles, while external people might want to have the official readable name.
  @Field(() => String)
  public aliases: Maybe<string>;

  // TODO we shouldnt need this
  override async validate(
    options?: ValidationMeta & {
      validateOmitted?: boolean;
    },
  ): Promise<void> {}

  override async create(): Promise<string> {
    this._id = this.name;
    return super.create();
  }

  override async remove(): Promise<boolean> {
    const relationships = await SemanticRelationship.getRelationshipsByPredicates(this._id)({ _class: 1, _id: 1 });
    if (relationships.length) {
      throw new Error(`Es existieren noch ${relationships.length} Nutzer mit dieser Rolle!`);
    }
    return super.remove();
  }

  @Query(() => ChancenRole)
  public static getChancenRoleByPredefinedRole(@Arg('roleString', () => String) roleString: string) {
    return async (gqlFields: BaseQLSelectionSet<ChancenRole>) => {
      const col = await getCollection<ChancenRole>(this._class);
      const role = constructValue(await col.findOne({ name: roleString })) as ChancenRole;
      return role;
    };
  }

  public static getLegacyRoleString(roleID: Role | string): string {
    switch (roleID) {
      case Role.STUDENT:
        return 'Schüler:in';
      case Role.ADMIN:
        return 'Admin';
      case Role.EDITOR:
        return 'Redakteur:in';
      case Role.LEKO:
        return 'LeKo';
      case Role.SCHUKO:
        return 'SchuKo';
      case Role.EXTERNAL_CONTACT:
        return 'Externe Kontaktperson';
      case Role.EXTERNAL_CONTACT_BUT:
        return 'Externe Kontaktperson BuT';
      case Role.EXTERNAL_TEACHER:
        return 'Externe Lehrperson';
      case Role.IKL:
        return 'Intensiv Kursleitung';
      case Role.OSC:
        return 'OS-Coach';
      case Role.DKL:
        return 'Digital Kursleitung';
      case Role.PROGRAM_MANAGEMENT:
        return 'Programmleitung';
      case Role.ASSISTANT_PROGRAM_MANAGEMENT:
        return 'Assistenz Programmleitung';
      case Role.SCHOOL_SUPPORT:
        return 'Schulbegleitungsteam';
      case Role.LOGISTICS_STAFF:
        return 'Logistikmitarbeiter:in';
      case Role.INTERNAL_KPF_STRATEGY:
        return 'Ansprechsperson Intern (Strategisch)';
      case Role.INTERNAL_KPF_OPERATIV:
        return 'Ansprechsperson Intern (Operativ)';
      case Role.INTERNAL_KPF_ADMINISTRATION:
        return 'Ansprechsperson Intern (Verwaltung)';
      case Role.INTERNAL_KPF_BUT_OPERATIV:
        return 'Ansprechsperson Intern (BuT Operativ)';
      case Role.HEFTE_TEACHER:
        return 'Hefte-Lehrkraft';
      case Role.HEFTE_STUDENT:
        return 'Hefte-Schüler:in';

      default:
        return `Rolle '${roleID}' nicht gefunden`;
    }
  }

  @Subscription(() => [ChancenRole])
  static subscribeRolesPaginatedFiltered(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => RoleFilter) filter: RoleFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<ChancenRole>) => {
      const selector = filter.getMongoFilter();

      return this.subscribePaginatedAggregation(skip, limit, selector, [
        { $addFields: { lowercaseName: { $toLower: '$readableName' } } },
        { $sort: { lowercaseName: 1 } },
      ]);
    };
  }

  override toString() {
    if (this.readableName) return this.readableName;
    return ChancenRole.getLegacyRoleString(this._id);
  }
}
